import { toastr } from "react-redux-toastr";
import { useForm } from "../../../hooks/useForm";
import {
  useGenerarReporteMutation,
  useGetReportesQuery,
} from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";
import { ReportesComponent } from "../../../components/ReportesComponet";

export const ReportesScreen = () => {
  // Queries & Mutations
  const { data, isLoading } = useGetReportesQuery();
  const [generarAction, { data: reporte, isLoading: reporteLoading }] = useGenerarReporteMutation();

  // Custom Form Hook
  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fi_reporte: "",
    fc_inicial: "",
    fc_final: "",
  });

  const generarReporte = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validaciones
    if (!formulario.fi_reporte || +formulario.fi_reporte === 0) {
      toastr.error("Debe seleccionar un tipo de reporte");
      return;
    }
    if (!formulario.fc_inicial) {
      toastr.error("Debe ingresar una Fecha Inicial");
      return;
    }
    if (!formulario.fc_final) {
      toastr.error("Debe ingresar una Fecha Final");
      return;
    }

    // Validación lógica de fechas
    if (new Date(formulario.fc_inicial) > new Date(formulario.fc_final)) {
      toastr.warning("La Fecha Inicial no puede ser posterior a la Fecha Final");
      return;
    }

    try {
      await generarAction({
        ...formulario,
        fi_reporte: +formulario.fi_reporte,
      }).unwrap();

      toastr.success("Reporte generado exitosamente");
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al generar el reporte");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* --- Encabezado y Filtros --- */}
      <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">

        {/* Cabecera */}
        <div className="flex flex-col gap-2 border-b border-slate-100 px-7 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900">
              <svg className="h-5 w-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
              Módulo de Reportes
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Genera extractos de datos y estadísticas operativas por rango de fechas.
            </p>
          </div>
        </div>

        {/* Formulario de Filtros */}
        <form onSubmit={generarReporte} className="bg-slate-50/50 p-6">
          <div className="grid grid-cols-1 items-end gap-5 lg:grid-cols-12">

            {/* Select: Tipo de Reporte */}
            <div className="lg:col-span-4">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fi_reporte">
                Tipo de Reporte
              </label>
              <select
                id="fi_reporte"
                name="fi_reporte"
                onChange={handleSelectChange}
                value={formulario.fi_reporte}
                className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="" disabled>Selecciona el reporte...</option>
                {data?.reportes?.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.fc_nombre_reporte}
                  </option>
                ))}
              </select>
            </div>

            {/* Input: Fecha Inicial */}
            <div className="lg:col-span-3">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_inicial">
                Fecha Inicial
              </label>
              <input
                id="fc_inicial"
                name="fc_inicial"
                type="date"
                onChange={handleInputChange}
                value={formulario.fc_inicial}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            {/* Input: Fecha Final */}
            <div className="lg:col-span-3">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_final">
                Fecha Final
              </label>
              <input
                id="fc_final"
                name="fc_final"
                type="date"
                onChange={handleInputChange}
                value={formulario.fc_final}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            {/* Botón Generar */}
            <div className="lg:col-span-2">
              <button
                type="submit"
                disabled={reporteLoading}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-70"
              >
                {reporteLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                    <span>Generar</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </form>
      </section>

      {/* --- Área de Resultados --- */}
      <div className="space-y-4">

        {/* Loading State */}
        {reporteLoading && (
          <div className="flex w-full flex-col items-center justify-center py-12 text-slate-500">
            <Spinner />
            <span className="mt-3 text-sm font-medium">Extrayendo y procesando datos...</span>
          </div>
        )}

        {/* Empty State (Sin Resultados) */}
        {!reporteLoading && reporte?.reporte && reporte.reporte.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center shadow-sm">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>
            </div>
            <p className="text-sm font-bold text-slate-700">No se encontraron registros</p>
            <p className="mt-1 text-xs text-slate-500">Intenta ampliar el rango de fechas o seleccionar otro tipo de reporte.</p>
          </div>
        )}

        {/* Componente de Resultados (Tabla/Gráficos) */}
        {!reporteLoading && reporte?.reporte && reporte.reporte.length > 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ReportesComponent data={reporte.reporte} />
          </div>
        )}

      </div>
    </div>
  );
};
